import { api } from '@/lib/api'

/**
 * @typedef {object} Customer360VisitService
 * @property {number} service_id
 * @property {string} service_name
 * @property {string | null} [name_ar]
 * @property {number | null} [stylist_id]
 * @property {string | null} [stylist_name]
 * @property {number | null} [chair_id]
 * @property {string | null} [chair_name]
 * @property {string} line_total
 */

/**
 * @typedef {object} Customer360Visit
 * @property {number} sale_id
 * @property {string} sale_number
 * @property {'completed' | 'refunded' | string} status
 * @property {string | null} completed_at
 * @property {string} subtotal
 * @property {string} discount_amount
 * @property {string} total
 * @property {number} loyalty_points_earned
 * @property {number} loyalty_points_redeemed
 * @property {Customer360VisitService[]} services
 */

/**
 * @typedef {object} Customer360Profile
 * @property {import('@/types/api').Customer} customer
 * @property {{
 *   visit_count: number
 *   completed_visit_count: number
 *   refunded_visit_count: number
 *   lifetime_spend: string
 *   refunded_total: string
 *   net_spend: string
 *   average_ticket: string
 *   last_visit_at: string | null
 *   loyalty_enabled: boolean
 *   loyalty_points_balance: number
 * }} summary
 * @property {Array<{
 *   service_id: number
 *   service_name: string
 *   name_ar?: string | null
 *   visit_count: number
 *   spend_total: string
 * }>} favourite_services
 * @property {Customer360Visit[]} visits
 */

/**
 * @param {number} customerId
 * @param {{ limit?: number, source?: 'masters' | 'pos' }} [options]
 * @returns {Promise<Customer360Profile>}
 */
export async function fetchCustomer360(customerId, options = {}) {
  const { limit = 25, source = 'masters' } = options
  const base = source === 'pos' ? '/customers' : '/masters/customers'
  const { data } = await api.get(`${base}/${customerId}/360`, { params: { limit } })
  return data.data.customer_360
}

/**
 * @param {string | number | null | undefined} value
 */
export function formatCustomer360Amount(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 'AED 0.00'
  return `AED ${amount.toFixed(2)}`
}

/**
 * @param {string | null | undefined} value
 */
export function formatCustomer360Date(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}
